import { cn } from '@/lib/utils';
import { kr } from '../../lib/money';
import { Panel } from '../common/Panel';
import { ChartSkeleton } from '../common/Skeleton';
import { CostHistoryChart } from './CostHistoryChart';
import {
  type CostHistory,
  periodLabel,
  periodRangeLabel,
  RANGE_OPTIONS,
  type RangeOption,
} from './cost-history';

/** Average over the finished periods that had payments (the ongoing one is still filling up) */
export function averageOfFinishedPeriods(
  points: Array<{ amount: number; paymentCount: number }>,
): number | null {
  const finished = points.slice(0, -1).filter((p) => p.paymentCount > 0);
  const source = finished.length > 0 ? finished : points.filter((p) => p.paymentCount > 0);
  if (source.length === 0) return null;
  return Math.round(source.reduce((sum, p) => sum + p.amount, 0) / source.length);
}

export function RangeToggle({
  value,
  onChange,
}: {
  value: RangeOption;
  onChange: (value: RangeOption) => void;
}) {
  return (
    <fieldset className="m-0 grid min-w-0 grid-cols-3 gap-0.5 rounded-lg border-0 bg-muted p-0.5">
      <legend className="sr-only">Antal perioder</legend>
      {RANGE_OPTIONS.map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={value === option}
          aria-label={`${option} perioder`}
          onClick={() => onChange(option)}
          className={cn(
            'flex h-9 min-w-10 items-center justify-center rounded-md px-2 text-xs font-medium tabular-nums transition-colors sm:h-7',
            value === option
              ? 'bg-card text-foreground shadow-xs'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {option}
        </button>
      ))}
    </fieldset>
  );
}

export function Kpi({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string | undefined;
}) {
  return (
    <div className="min-w-0">
      {/* Long labels wrap rather than cut off on phones */}
      <div className="line-clamp-2 text-xs text-muted-foreground">{label}</div>
      <div className="truncate text-base font-semibold tabular-nums sm:text-lg">{value}</div>
      {hint && <div className="truncate text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

/** Sum of every bill per budget period, newest period highlighted. */
export function CostHistoryTotalCard({
  data,
  isLoading,
  range,
  onRangeChange,
}: {
  data: CostHistory | undefined;
  isLoading: boolean;
  range: RangeOption;
  onRangeChange: (range: RangeOption) => void;
}) {
  const totals = data?.totals ?? [];
  const current = totals.at(-1);
  const previous = totals.at(-2);
  const currentPeriod = data?.periods.at(-1);
  const average = averageOfFinishedPeriods(totals);
  const hasPayments = totals.some((t) => t.paymentCount > 0);

  return (
    <Panel
      title="Totalt per period"
      description="Alla fasta räkningar, per löneperiod"
      action={<RangeToggle value={range} onChange={onRangeChange} />}
    >
      {isLoading || !data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <ChartSkeleton height="2.75rem" />
            <ChartSkeleton height="2.75rem" />
            <ChartSkeleton height="2.75rem" />
          </div>
          <ChartSkeleton height="12rem" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Kpi
              label="Denna period"
              value={current && current.paymentCount > 0 ? kr(current.amount) : '–'}
              hint={currentPeriod ? periodRangeLabel(currentPeriod) : undefined}
            />
            <Kpi
              label="Förra perioden"
              value={previous && previous.paymentCount > 0 ? kr(previous.amount) : '–'}
              hint={previous ? periodLabel(previous.year, previous.month) : undefined}
            />
            <Kpi label="Snitt" value={average != null ? kr(average) : '–'} hint="per hel period" />
          </div>
          {hasPayments ? (
            <CostHistoryChart
              points={totals}
              average={average}
              height={{ base: 190, sm: 240 }}
              ariaLabel={`Fasta räkningar per period, senaste ${totals.length} perioderna`}
            />
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Inga betalningar de senaste {range} perioderna.
            </p>
          )}
        </div>
      )}
    </Panel>
  );
}
